<template>
    <div class="flex flex-wrap items-center gap-4 lg:gap-2">
        <div v-if="breadcrumbPrefix" class="group mt-2 mb-2 flex w-full items-center gap-2 sm:mt-1 sm:mb-1 sm:w-auto">
            <NuxtLink
                class="hidden lg:inline-block font-mori rounded-sm bg-white px-2 py-2 text-xs leading-[0] duration-300 hover:text-green-500 min-[351px]:text-sm lg:bg-transparent lg:p-0 lg:pr-5 lg:text-sm"
                @click.prevent="handleClick(getPath(breadcrumbPrefix.url))"
                :to="getPath(breadcrumbPrefix.url)">
                <NuxtImg
                    :alt="snippets['headless.breadcrumbs.arrowLeft']"
                    class="inline size-5 break-words lg:mr-2 lg:size-3"
                    width="12"
                    height="12"
                    src="/icons/arrow-left.svg" />
                <span class="hidden lg:inline-block">{{ breadcrumbPrefix.name }}</span>
            </NuxtLink>
            <button
                class="lg:hidden font-mori rounded-sm bg-white px-2 py-2 text-xs leading-[0] duration-300 hover:text-green-500 min-[351px]:text-sm lg:bg-transparent lg:p-0 lg:pr-5 lg:text-sm"
                @click.prevent="router.back()">
                <NuxtImg
                    :alt="snippets['headless.breadcrumbs.arrowLeft']"
                    class="inline size-5 break-words lg:mr-2 lg:size-3"
                    width="12"
                    height="12"
                    src="/icons/arrow-left.svg" />
                <span class="hidden lg:inline-block">{{ breadcrumbPrefix.name }}</span>
            </button>
        </div>

        <!-- Trail only from lg: below that it is absolutely positioned over the PDP image and
             collides with the wishlist heart (tablet gets the mobile back-pill instead) -->
        <div
            class="group hidden items-center gap-1 lg:flex lg:gap-2"
            v-for="(param, index) in breadcrumbs"
            :key="index">
            <NuxtLink
                class="font-mori text-xs leading-[0] capitalize duration-300 hover:text-green-500 lg:text-sm"
                :to="getPath(param.url)"
                >{{ param.name }}
            </NuxtLink>

            <div
                class="font-mori pl-2 text-xs leading-[0] text-gray-300 capitalize lg:pl-1 lg:text-sm"
                v-if="index < breadcrumbs.length - 1">
                /
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue';
    import { getBreadcrumbPrefix, handleBreadCrumbPrefixClick } from '~/composables/useBreadcrumbPrefix';

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    interface IBreadcrumb {
        name: string;
        url: string;
    }

    const path = inject('breadcrumbs') as string | IBreadcrumb[] | undefined;
    const router = useRouter();

    const breadcrumbPrefix = computed(() => getBreadcrumbPrefix());

    const currentRoute = ref(router.currentRoute);
    const breadcrumbs: Ref<IBreadcrumb[]> = ref([]);

    if (path && path.length > 0) {
        breadcrumbs.value = (path as IBreadcrumb[]).map(b =>
            b.name?.toLowerCase() === 'home' ? { ...b, url: '/nl/' } : b,
        );
    } else {
        breadcrumbs.value = generateBreadcrumbs(currentRoute.value.fullPath);
    }

    function getPath(url: string): string {
        if (!/^https?:\/\//i.test(url)) {
            return url;
        }

        const urlObject = new URL(url);
        return urlObject.pathname;
    }

    function handleClick(targetPath: string) {
        // On mobile the button shows only an arrow, so it should always act as a back button.
        const isMobile = window.matchMedia('(max-width: 1023px)').matches;

        if (isMobile) {
            if (window.history.state?.back) {
                router.back();
            } else {
                router.push(targetPath);
            }
            return;
        }

        handleBreadCrumbPrefixClick(targetPath);
    }

    function generateBreadcrumbs(path: string): IBreadcrumb[] {
        let breadcrumbs = [];

        const cleanPath = path.split('?')[0];
        const segments = cleanPath.split('/').filter(segment => segment !== '');

        breadcrumbs = [
            {
                name: 'Home',
                url: '/nl/',
            },
        ];

        segments.forEach((segment, index) => {
            // if segment is Nl, remove it
            if (segment === 'nl') {
                return;
            }

            breadcrumbs.push({
                name: segment.replace(/-/g, ' '),
                url: '/' + segments.slice(0, index + 1).join('/') + '/',
            });
        });

        return breadcrumbs;
    }
</script>
